import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useQuery } from "convex/react";
import { useMemo } from "react";

import {
  locationSalesGetSalesByEmployee,
  type SalesByEmployeeEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type StaffSalesData = SalesByEmployeeEntry;

interface DailySalesViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const DailySalesView = ({
  locationId,
  dateRange,
  tableId,
}: DailySalesViewProps) => {
  const data = useQuery(locationSalesGetSalesByEmployee, {
    dateRange: {
      from: dateRange.from.getTime(),
      to: dateRange.to.getTime(),
    },
    locationId: toConvexTableId("locations", locationId),
  });

  const columnHelper = createColumnHelper<StaffSalesData>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("location", {
          header: "Location",
          cell: ({ getValue }) => getValue().name,
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.location.id),
          meta: {
            faceting: {
              title: "Location",
              getOption: (r: StaffSalesData["location"]) => ({
                value: r.id,
                label: r.name,
              }),
            },
            getCellForExport: (r: StaffSalesData["location"]) => r.name,
          },
        }),
        columnHelper.accessor("employee", {
          header: "Employee",
          cell: ({ getValue }) => {
            const employee = getValue();
            return (
              <div className="flex items-center gap-3">
                <img
                  alt={`${employee.firstName} ${employee.lastName}'s avatar`}
                  className="size-6 rounded-full"
                  height={24}
                  src={employee.imageUrl ?? "/images/avatar-placeholder.svg"}
                  width={24}
                />
                <p>
                  {employee.firstName} {employee.lastName}
                </p>
              </div>
            );
          },
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.employee.id),
          meta: {
            getCellForExport: (e: StaffSalesData["employee"]) =>
              `${e.firstName} ${e.lastName}`,
            faceting: {
              title: "Employee",
              getOption: (e: StaffSalesData["employee"]) => ({
                value: e.id,
                label: `${e.firstName} ${e.lastName}`,
              }),
            },
          },
        }),
        columnHelper.accessor("billsClosed", {
          header: "Bills Closed",
          cell: ({ getValue }) => getValue()?.toLocaleString("en-US"),
          meta: {
            getCellForExport: (d: StaffSalesData["billsClosed"]) => d,
          },
        }),
        columnHelper.accessor("hoursWorked", {
          header: "Hours Worked",
          cell: ({ getValue }) => getValue()?.toFixed(1),
          meta: {
            getCellForExport: (d: StaffSalesData["hoursWorked"]) => d,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: StaffSalesData["amountSold"]) => d,
          },
        }),
        columnHelper.accessor("shareOfTotalSales", {
          header: "Sales %",
          cell: ({ getValue }) => `${(getValue() * 100)?.toFixed(1)}%`,
          meta: {
            getCellForExport: (d: StaffSalesData["shareOfTotalSales"]) => d,
          },
        }),
        columnHelper.display({
          id: "avgSalesPerHour",
          header: "Avg Sales/Hour",
          cell: ({ row }) => {
            const hoursWorked = row.original.hoursWorked;
            const amountSold = row.original.amountSold;
            if (!hoursWorked || hoursWorked === 0) {
              return "$0.00";
            }
            const avgPerHour = amountSold / hoursWorked;
            return avgPerHour.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: StaffSalesData) => {
              const hoursWorked = row.hoursWorked;
              const amountSold = row.amountSold;
              if (!hoursWorked || hoursWorked === 0) {
                return 0;
              }
              return amountSold / hoursWorked;
            },
          },
        }),
        columnHelper.display({
          id: "avgSalesPerBill",
          header: "Avg Sales/Bill",
          cell: ({ row }) => {
            const billsClosed = row.original.billsClosed;
            const amountSold = row.original.amountSold;
            if (!billsClosed || billsClosed === 0) {
              return "$0.00";
            }
            const avgPerBill = amountSold / billsClosed;
            return avgPerBill.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            });
          },
          meta: {
            getCellForExport: (row: StaffSalesData) => {
              const billsClosed = row.billsClosed;
              const amountSold = row.amountSold;
              if (!billsClosed || billsClosed === 0) {
                return 0;
              }
              return amountSold / billsClosed;
            },
          },
        }),
      ] as ColumnDef<StaffSalesData>[],
    [columnHelper]
  );

  if (data === undefined) {
    return <LoadingPanel message="Loading employee sales data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
